/**
 * Deeply merges multiple objects into a new object.
 * Later sources override earlier ones (right-to-left precedence).
 * - Objects are merged recursively
 * - Arrays are **replaced** (not concatenated) by default
 * - null/undefined values from source are skipped
 *
 * @example
 * deepMerge({ a: 1, b: { x: 10 } }, { b: { y: 20 }, c: 3 })
 * // → { a: 1, b: { x: 10, y: 20 }, c: 3 }
 */
export function deepMerge<T>(obj1: T): T
export function deepMerge<T, U>(obj1: T, obj2: U): T & U
export function deepMerge<T, U, V>(obj1: T, obj2: U, obj3: V): T & U & V
export function deepMerge<T, U, V, W>(obj1: T, obj2: U, obj3: V, obj4: W): T & U & V & W
export function deepMerge<T = unknown>(
  target: Partial<T> | null | undefined,
  ...sources: Array<Partial<T> | null | undefined>
): T {
  // We'll build the result starting from a fresh object
  const result = structuredClone(target ?? {}) as Record<string, unknown>

  for (const source of sources) {
    if (!source || typeof source !== 'object') {
      continue
    }

    for (const key in source) {
      const sourceValue = (source as Record<string, unknown>)[key]

      // Skip undefined values
      if (sourceValue === undefined) {
        continue
      }

      const targetValue = result[key]

      // Both are objects → recursive merge
      if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
        result[key] = deepMerge(targetValue, sourceValue)
        continue
      }

      // Otherwise: replace (including arrays, primitives, null, etc.)
      result[key] = sourceValue
    }
  }

  return result as T
}

/**
 * Check if value is a plain JavaScript object (not null, not array, not class instance, etc.)
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false
  }

  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}
